import type { RevisionEntry } from '@/utils/revisionHistorial';
import { AlertCircle, ChevronDown, ChevronUp, MessageSquare, CheckCircle } from 'lucide-react';
import { useState } from 'react';

interface Props {
  entries: RevisionEntry[];
  rondaActual: number;
  className?: string;
}

function formatFecha(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-CO', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function RechazoMatrizCollapse({ entries, rondaActual, className = '' }: Props) {
  const [abierto, setAbierto] = useState(false);

  // Filtrar las entradas de rechazo de la ronda actual
  const rechazoRondaActual = [...entries].reverse().find((e) => e.estado === 'RECHAZADO' && e.ronda === rondaActual);

  if (!rechazoRondaActual) return null;

  const ultimaRespuesta = [...entries]
    .reverse()
    .find((e) => (e.estado === 'RESPUESTA_ASESOR' || e.estado === 'CORREGIDO') && e.ronda === rondaActual);

  const previos = entries.filter((e) => e.id !== rechazoRondaActual.id && e.id !== ultimaRespuesta?.id);

  return (
    <div className={`mt-1.5 rounded-md border border-[#f5c6c0] bg-[#fdf0ee] px-2.5 py-1.5 ${className}`}>
      <div className="flex flex-col gap-1">
        <div className="flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#c0392b]" />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] whitespace-pre-line text-[#c0392b]">
              <span className="font-semibold">
                Rechazo en ronda {rondaActual}
                {rechazoRondaActual.adminNombre ? ` (${rechazoRondaActual.adminNombre})` : ''}:
              </span>{' '}
              {rechazoRondaActual.observacion ? rechazoRondaActual.observacion : 'sin comentario'}
              <span className="text-[#c0392b]/60"> · {formatFecha(rechazoRondaActual.fecha)}</span>
            </p>
          </div>
        </div>

        {ultimaRespuesta && (
          <div className="pl-5 mt-1 border-l border-dashed border-[#bbf7d0] ml-1.5 flex items-start gap-1.5 text-[10px] text-[#166534]">
            <CheckCircle className="w-3 h-3 mt-0.5 shrink-0 text-[#166534]" />
            <div className="flex-1 min-w-0 whitespace-pre-line">
              <span className="font-semibold">
                {ultimaRespuesta.estado === 'CORREGIDO' ? 'Corregido en borrador' : 'Respuesta/Aclaración enviada'}:
              </span>{' '}
              {ultimaRespuesta.observacion ? ultimaRespuesta.observacion : 'sin comentario'}
              <span className="text-[#166534]/70"> · {formatFecha(ultimaRespuesta.fecha)}</span>
            </div>
          </div>
        )}

        {(previos.length > 0 || ultimaRespuesta) && (
          <button
            type="button"
            onClick={() => setAbierto((v) => !v)}
            className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-[#c0392b] hover:underline self-start"
          >
            {abierto ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {abierto ? 'Ocultar historial' : `Ver historial completo (${previos.length + (ultimaRespuesta ? 1 : 0)})`}
          </button>
        )}
      </div>

      {abierto && (
        <ol className="mt-1.5 border-t border-[#f5c6c0] pt-1.5 space-y-1">
          {previos.map((e) => {
            const esAsesor = e.estado === 'RESPUESTA_ASESOR' || e.estado === 'CORREGIDO';
            return (
              <li key={e.id} className="flex items-start gap-1.5 text-[10px]">
                {esAsesor ? (
                  <MessageSquare className="w-3 h-3 mt-0.5 shrink-0 text-[#2b6cb0]" />
                ) : (
                  <AlertCircle className="w-3 h-3 mt-0.5 shrink-0 text-[#c0392b]" />
                )}
                <span className="text-gray-600 whitespace-pre-line text-[10px]">
                  <span className="font-semibold" style={{ color: esAsesor ? '#2b6cb0' : '#c0392b' }}>
                    {esAsesor ? 'Respuesta/Corrección del asesor' : `Rechazo${e.adminNombre ? ` · ${e.adminNombre}` : ''}`}
                  </span>
                  {e.observacion ? `: ${e.observacion}` : ''}
                  <span className="text-gray-400"> · {formatFecha(e.fecha)}</span>
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
